import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { UploadService, UploadConfig } from '../services/upload/upload.service';
import { TemplateService } from '../services/upload/template.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { lastValueFrom } from 'rxjs';

export interface ValidationError {
  row: number;
  column: string;
  value: string;
  error: string;
}

@Component({
  selector: 'app-bulk-upload',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    PaginatorModule
  ],
  templateUrl: './bulk-upload.html',
  styleUrls: ['./bulk-upload.scss']
})
export class BulkUploadComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() module: string = '';
  @Input() title: string = 'Bulk Upload';
  @Input() formTemplateId?: string | number;
  @Input() additionalParams?: Record<string, string | number>;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() uploadSuccess = new EventEmitter<any>();
  @Output() uploadError = new EventEmitter<any>();

  uploadForm!: FormGroup;
  selectedFile: File | null = null;
  fileName: string = '';
  isUploading: boolean = false;
  validationErrors: ValidationError[] = [];
  showValidationErrors: boolean = false;
  errorMessage: string = '';
  successCount: number = 0;
  errorCount: number = 0;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  constructor(
    private fb: FormBuilder,
    private uploadService: UploadService,
    private templateService: TemplateService,
    private toasterService: ToasterService
  ) {}

  ngOnInit() {
    this.uploadForm = this.fb.group({
      file: [null, Validators.required]
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible'] && !changes['visible'].currentValue) {
      // Reset component when dialog is closed
      this.resetComponent();
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.fileName = this.selectedFile.name;
      this.uploadForm.patchValue({
        file: this.selectedFile
      });
    }
  }

  async onUpload(): Promise<void> {
    if (this.uploadForm.valid && this.selectedFile && this.module) {
      this.isUploading = true;
      
      try {
        const config: UploadConfig = {
          module: this.module,
          formTemplateId: this.formTemplateId,
          additionalParams: this.additionalParams
        };

        const response = await lastValueFrom(
          this.uploadService.uploadFile(this.selectedFile, config)
        );

        // Check if response has validation errors (even if success is true)
        if (response?.validation_errors && Array.isArray(response.validation_errors) && response.validation_errors.length > 0) {
          // Has validation errors - show them
          this.handleErrorResponse(response);
        } else if (response?.success === true || response?.success === undefined) {
          // True success - no validation errors
          this.toasterService.success('Success', response?.message || 'File uploaded successfully');
          this.uploadSuccess.emit(response);
          this.onClose();
        } else {
          // Handle other error cases
          this.handleErrorResponse(response);
        }
      } catch (error: any) {
        this.handleErrorResponse(error);
      } finally {
        this.isUploading = false;
      }
    }
  }

  handleErrorResponse(error: any): void {
    const errorResponse = error?.error || error;
    
    // Check if it's a validation error response
    if (errorResponse?.validation_errors && Array.isArray(errorResponse.validation_errors)) {
      this.validationErrors = errorResponse.validation_errors;
      this.errorMessage = errorResponse?.message || 'CSV validation failed';
      this.successCount = errorResponse?.success_count || 0;
      this.errorCount = errorResponse?.error_count || this.validationErrors.length;
      this.showValidationErrors = true;
      this.toasterService.error('Validation Error', this.errorMessage);
    } else {
      // Generic error
      const errorMsg = errorResponse?.message || errorResponse?.error || error?.message || 'Failed to upload file';
      this.toasterService.error('Error', errorMsg);
      this.uploadError.emit(error);
    }
  }

  onClose(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetComponent();
  }

  resetComponent(): void {
    this.uploadForm?.reset();
    this.selectedFile = null;
    this.fileName = '';
    this.isUploading = false;
    this.validationErrors = [];
    this.showValidationErrors = false;
    this.errorMessage = '';
    this.successCount = 0;
    this.errorCount = 0;
    // Reset file input
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  closeValidationErrors(): void {
    this.showValidationErrors = false;
    this.validationErrors = [];
  }

  onBack(): void {
    this.resetComponent();
    this.visible = false;
    this.visibleChange.emit(false);
  }

  removeFile(): void {
    this.selectedFile = null;
    this.fileName = '';
    this.uploadForm.patchValue({
      file: null
    });
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.selectedFile = event.dataTransfer.files[0];
      this.fileName = this.selectedFile.name;
      this.uploadForm.patchValue({
        file: this.selectedFile
      });
    }
  }

  getFileSize(bytes: number | null | undefined): string {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  }

  openFileDialog(): void {
    this.fileInput?.nativeElement?.click();
  }

  downloadTemplate(): void {
    if (!this.module) {
      this.toasterService.error('Error', 'Module not specified');
      return;
    }

    try {
      this.templateService.downloadTemplate(this.module);
      // Don't show toast immediately - let the download happen first
      setTimeout(() => {
        this.toasterService.success('Success', 'Template downloaded successfully');
      }, 200);
    } catch (error) {
      this.toasterService.error('Error', 'Failed to download template');
    }
  }
}

